import React, { useState } from "react";
import { Client } from "../types";

interface ClientDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    client: Client | null;
    onApprove: (client: Client) => void;
    onReject: (client: Client) => void;
    onDelete: (client: Client) => void;
}

const API_URL = process.env.REACT_APP_API_URL || "";

const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
    isOpen,
    onClose,
    client,
    onApprove,
    onReject,
    onDelete,
}) => {
    const [activeTab, setActiveTab] = useState<"info" | "documents">("info");
    const [viewingImage, setViewingImage] = useState<string | null>(null);
    const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({
        cni_recto: true,
        cni_verso: true,
        photo_tete: true,
    });

    if (!isOpen || !client) return null;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleImageLoad = (key: string) => {
        setImageLoading((prev) => ({ ...prev, [key]: false }));
    };

    const DocumentCard = ({
        label,
        imageKey,
        imagePath,
        icon,
    }: {
        label: string;
        imageKey: string;
        imagePath: string;
        icon: React.ReactNode;
    }) => (
        <div className="group relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-accent/50 to-accent-light/50 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
            <div className="relative bg-primary-dark rounded-xl overflow-hidden border border-white/10">
                {/* Header */}
                <div className="flex items-center space-x-2 px-4 py-3 bg-primary-light/30 border-b border-white/10">
                    <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center text-accent">
                        {icon}
                    </div>
                    <span className="text-sm font-medium text-white">
                        {label}
                    </span>
                </div>

                {/* Image */}
                <div
                    className="relative aspect-[4/3] cursor-pointer overflow-hidden"
                    onClick={() => setViewingImage(`${API_URL}/${imagePath}`)}
                >
                    {imageLoading[imageKey] && (
                        <div className="absolute inset-0 bg-primary-light animate-pulse flex items-center justify-center">
                            <svg
                                className="w-8 h-8 text-gray-600 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                        </div>
                    )}
                    <img
                        src={`${API_URL}/${imagePath}`}
                        alt={label}
                        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                            imageLoading[imageKey] ? "opacity-0" : "opacity-100"
                        }`}
                        onLoad={() => handleImageLoad(imageKey)}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src =
                                "/images/placeholder.jpg";
                            handleImageLoad(imageKey);
                        }}
                    />

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-4">
                        <div className="flex items-center space-x-2 text-white text-sm font-medium bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                />
                            </svg>
                            <span>Agrandir</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md animate-[fadeIn_0.2s_ease-out]"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="relative w-full max-w-3xl bg-gradient-to-b from-primary-light to-primary-dark rounded-3xl shadow-2xl shadow-black/50 pointer-events-auto animate-[slideUp_0.3s_ease-out] max-h-[90vh] flex flex-col overflow-hidden border border-white/10"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header with gradient */}
                    <div className="relative px-6 pt-6 pb-4 bg-gradient-to-r from-accent/10 via-transparent to-accent/10">
                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        {/* Client avatar and info */}
                        <div className="flex items-center space-x-4">
                            <div className="relative">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent to-accent-light flex items-center justify-center shadow-lg shadow-accent/25">
                                    <span className="text-primary-dark text-3xl font-bold">
                                        {client.username
                                            .charAt(0)
                                            .toUpperCase()}
                                    </span>
                                </div>
                                {/* Status indicator */}
                                <div
                                    className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-primary-light flex items-center justify-center ${
                                        client.is_approved
                                            ? "bg-green-500"
                                            : "bg-yellow-500"
                                    }`}
                                >
                                    {client.is_approved ? (
                                        <svg
                                            className="w-3 h-3 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            className="w-3 h-3 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M12 8v4l3 3"
                                            />
                                        </svg>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-white mb-1">
                                    {client.username}
                                </h2>
                                <div className="flex items-center space-x-3">
                                    <span className="text-gray-500 text-sm">
                                        ID: {client.id}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex px-6 border-b border-white/10">
                        <button
                            onClick={() => setActiveTab("info")}
                            className={`relative px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === "info"
                                    ? "text-accent"
                                    : "text-gray-400 hover:text-white"
                            }`}
                        >
                            <span className="flex items-center space-x-2">
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                </svg>
                                <span>Informations</span>
                            </span>
                            {activeTab === "info" && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-full" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab("documents")}
                            className={`relative px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === "documents"
                                    ? "text-accent"
                                    : "text-gray-400 hover:text-white"
                            }`}
                        >
                            <span className="flex items-center space-x-2">
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                                <span>Documents</span>
                            </span>
                            {activeTab === "documents" && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-full" />
                            )}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {activeTab === "info" ? (
                            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
                                {/* Info cards grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Username card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                                                <svg
                                                    className="w-5 h-5 text-accent"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Nom d'utilisateur
                                                </p>
                                                <p className="text-white font-semibold">
                                                    {client.username}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ID card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                                <svg
                                                    className="w-5 h-5 text-blue-400"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Identifiant
                                                </p>
                                                <p className="text-white font-semibold">
                                                    #{client.id}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Date card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                                <svg
                                                    className="w-5 h-5 text-purple-400"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Date d'inscription
                                                </p>
                                                <p className="text-white font-semibold">
                                                    {formatDate(
                                                        client.created_at,
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div
                                                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                    client.is_approved
                                                        ? "bg-green-500/10"
                                                        : "bg-yellow-500/10"
                                                }`}
                                            >
                                                {client.is_approved ? (
                                                    <svg
                                                        className="w-5 h-5 text-green-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="w-5 h-5 text-yellow-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Statut
                                                </p>
                                                <p
                                                    className={`font-semibold ${client.is_approved ? "text-green-400" : "text-yellow-400"}`}
                                                >
                                                    {client.is_approved
                                                        ? "Approuve"
                                                        : "En attente"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Role info */}
                                <div className="bg-gradient-to-r from-accent/5 to-transparent rounded-xl p-4 border border-accent/20">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                                            <svg
                                                className="w-6 h-6 text-accent"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                Role
                                            </p>
                                            <p className="text-white font-semibold capitalize">
                                                {client.role}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="animate-[fadeIn_0.3s_ease-out]">
                                <p className="text-gray-400 text-sm mb-6">
                                    Documents d'identite fournis lors de
                                    l'inscription. Cliquez sur une image pour
                                    l'agrandir.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <DocumentCard
                                        label="CNI Recto"
                                        imageKey="cni_recto"
                                        imagePath={client.cni_recto}
                                        icon={
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                                                />
                                            </svg>
                                        }
                                    />
                                    <DocumentCard
                                        label="CNI Verso"
                                        imageKey="cni_verso"
                                        imagePath={client.cni_verso}
                                        icon={
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                                                />
                                            </svg>
                                        }
                                    />
                                    <DocumentCard
                                        label="Photo de tete"
                                        imageKey="photo_tete"
                                        imagePath={client.photo_tete}
                                        icon={
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                            </svg>
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer actions */}
                    <div className="px-6 py-4 bg-primary-dark/50 border-t border-white/10">
                        {client.is_approved ? (
                            /* Actions pour les clients approuvés */
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <button
                                    onClick={() => {
                                        onClose();
                                        onDelete(client);
                                    }}
                                    className="flex items-center space-x-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium">
                                        Supprimer
                                    </span>
                                </button>

                                <button
                                    onClick={onClose}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-200"
                                >
                                    Fermer
                                </button>
                            </div>
                        ) : (
                            /* Actions pour les clients en attente - uniquement Rejeter et Approuver */
                            <div className="flex items-center justify-end gap-3 px-4">
                                <button
                                    onClick={() => {
                                        onClose();
                                        onReject(client);
                                    }}
                                    className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-xl transition-all duration-200"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                    <span>Rejeter</span>
                                </button>
                                <button
                                    onClick={() => {
                                        onClose();
                                        onApprove(client);
                                    }}
                                    className="flex items-center space-x-2 px-5 py-2.5 text-sm font-semibold text-primary-dark bg-gradient-to-r from-green-500 to-green-400 hover:from-green-400 hover:to-green-300 rounded-xl shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-200"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                    <span>Approuver</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Image viewer */}
            {viewingImage && (
                <div
                    className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease-out]"
                    onClick={() => setViewingImage(null)}
                >
                    <button
                        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                        onClick={() => setViewingImage(null)}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                    <img
                        src={viewingImage}
                        alt="Document"
                        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl animate-[scaleIn_0.3s_ease-out]"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            {/* Styles */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.98);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
            `}</style>
        </>
    );
};

export default ClientDetailModal;
